import React, { useState } from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import { OnboardingInput } from "./OnboardingInput";
import OnboardingDropdown from "./OnboardingDropdown";
import { OnboardingButtonOutlined } from "../components/OnboardingButton";
import { validateAccountNumber } from "../utils/validationPatterns";
import {
  BodyShortLargeStrong,
  BodyShortMedium,
  LabelMedium,
} from "./OnboardingTypography";
import {
  OnboardingCard,
  MessageBox,
  FlexColumn,
  FlexRow,
  CompletedSection,
  THEME,
  ResponsiveFieldSmall,
  ResponsiveFieldLarge,
  Spinner,
  ButtonContent,
  DisabledButtonState,
} from "./OnboardingLayoutComponents";

// Removed - now using OnboardingCard from OnboardingLayoutComponents

const services = [
  { value: 0, label: "Velg" },
  { value: 1, label: "Inkassovarsel" },
  { value: 2, label: "Inkasso krav" },
];
const OnboardingSetupForm = ({
  companyName,
  accountNumber,
  setAccountNumber,
  selectedService,
  setSelectedService,
  mva,
  setMva,
  onCreateKosCreditor = null,
  kosCreditorCreated = false,
  kosCreditorLoading = false,
  kosCreditorError = null,
  hasSignedAgreement = false,
  skipSignature = false,
  stepStatus = "pending",
  stepNumber = 2,
}) => {
  const [isSaved, setIsSaved] = useState(kosCreditorCreated);

  // Validation state (only for accountNumber field)
  const [accountNumberTouched, setAccountNumberTouched] = useState(false);
  const [accountNumberError, setAccountNumberError] = useState("");

  // Update isSaved when kosCreditorCreated changes
  React.useEffect(() => {
    setIsSaved(kosCreditorCreated);
  }, [kosCreditorCreated]);

  // Blur handler for accountNumber field
  const handleAccountNumberBlur = () => {
    setAccountNumberTouched(true);
    const error = validateAccountNumber(accountNumber);
    setAccountNumberError(error);
  };

  // Calculate if form is valid (all fields must be filled correctly)
  const isFormValid =
    validateAccountNumber(accountNumber) === "" &&
    selectedService &&
    selectedService !== "0" &&
    parseInt(selectedService, 10) > 0 &&
    mva &&
    mva !== "Velg";

  const handleSave = async () => {
    if (onCreateKosCreditor && !kosCreditorCreated) {
      // Collect current form data to save before creating KOS creditor
      const formData = {
        accountNo: accountNumber,
        defaultProductNo: selectedService
          ? parseInt(selectedService, 10)
          : null,
        vatRequired: mva === "Ja",
      };
      await onCreateKosCreditor(formData);
    } else {
      setIsSaved(true);
    }
  };

  return (
    <OnboardingCard padding={THEME.spacing.xxl} gap={THEME.spacing.xxl}>
      {isSaved ? (
        <FlexRow flex="1" alignItems="center" justifyContent="space-between">
          <FlexColumn flex="1" gap={THEME.spacing.xxxl}>
            <FlexRow alignItems="baseline" gap={THEME.spacing.sm}>
              <span
                className="fa fa-check-circle"
                style={{ fontSize: "20px", color: "#000000" }}
              />
              <BodyShortLargeStrong>
                Steg {stepNumber}: Oppsett for {companyName}
              </BodyShortLargeStrong>
            </FlexRow>
            <FlexRow wrap="wrap" gap={THEME.spacing.xxl}>
              <ResponsiveFieldSmall>
                <FlexColumn gap={THEME.spacing.xs}>
                  <LabelMedium>Kontonummer for innbetalinger</LabelMedium>
                  <BodyShortMedium>{accountNumber}</BodyShortMedium>
                </FlexColumn>
              </ResponsiveFieldSmall>

              <ResponsiveFieldSmall>
                <FlexColumn gap={THEME.spacing.xs}>
                  <LabelMedium>Produkttype</LabelMedium>
                  <BodyShortMedium>
                    {services.find(
                      (service) => service.value === parseInt(selectedService),
                    )?.label || "Ikke valgt"}
                  </BodyShortMedium>
                </FlexColumn>
              </ResponsiveFieldSmall>

              <ResponsiveFieldSmall>
                <FlexColumn gap={THEME.spacing.xs}>
                  <LabelMedium>Mva. pliktig</LabelMedium>
                  <BodyShortMedium>{mva}</BodyShortMedium>
                </FlexColumn>
              </ResponsiveFieldSmall>
            </FlexRow>
          </FlexColumn>
          <div>
            <span
              className="fa fa-check-circle"
              style={{ fontSize: "40px", color: "#09AF06" }}
            />
          </div>
        </FlexRow>
      ) : (
        <FlexColumn gap={THEME.spacing.giant} flex="1">
          <FlexRow>
            <FlexRow flex="1">
              <FlexRow alignItems="baseline" gap={THEME.spacing.sm}>
                {stepStatus === "current" ? (
                  <span
                    className="fa fa-arrow-circle-right"
                    style={{
                      fontSize: "20px",
                      color: THEME.colors.primaryDark,
                    }}
                  />
                ) : (
                  <span
                    className="fa fa-circle-o"
                    style={{ fontSize: "20px", color: "#000000" }}
                  />
                )}
                <BodyShortLargeStrong>
                  Steg {stepNumber}: Oppsett for {companyName}
                </BodyShortLargeStrong>
              </FlexRow>
            </FlexRow>
          </FlexRow>
          <FlexRow wrap="wrap" gap={THEME.spacing.xxl} alignItems="flex-start">
            <ResponsiveFieldLarge>
              <OnboardingInput
                label="Kontonummer for innbetalinger"
                placeholder="Skriv inn kontonummer"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                onBlur={handleAccountNumberBlur}
                error={accountNumberTouched ? accountNumberError : ""}
                required={true}
              />
            </ResponsiveFieldLarge>

            <ResponsiveFieldLarge>
              <OnboardingDropdown
                label="Tjenester"
                items={services}
                onChange={(e) => setSelectedService(e.target.value)}
                defaultValue={selectedService}
                required={true}
              />
              <LabelMedium
                style={{
                  marginTop: THEME.spacing.sm,
                  fontSize: THEME.fontSize.sm,
                  color: THEME.colors.textMuted,
                  fontStyle: "italic",
                }}
              >
                <FormattedMessage id="serviceTypeHelp" />
              </LabelMedium>
            </ResponsiveFieldLarge>

            <ResponsiveFieldLarge>
              <OnboardingDropdown
                label="Mva. pliktig?"
                items={[
                  { value: "Velg", label: "Velg" },
                  { value: "Ja", label: "Ja" },
                  { value: "Nei", label: "Nei" },
                ]}
                defaultValue={mva}
                onChange={(e) => setMva(e.target.value)}
                required={true}
              />
              <LabelMedium
                style={{
                  marginTop: THEME.spacing.sm,
                  fontSize: THEME.fontSize.sm,
                  color: THEME.colors.textMuted,
                  fontStyle: "italic",
                }}
              >
                Dette feltet er påkrevd for å kunne lagre
              </LabelMedium>
            </ResponsiveFieldLarge>
          </FlexRow>

          {/* Show error if KOS creditor creation failed */}
          {kosCreditorError && (
            <MessageBox variant="error" margin={THEME.spacing.lg + " 0 0 0"}>
              {kosCreditorError}
            </MessageBox>
          )}

          <FlexColumn gap={THEME.spacing.sm}>
            <FlexRow
              justifyContent="flex-end"
              margin={THEME.spacing.lg + " 0 0 0"}
            >
              <DisabledButtonState
                disabled={
                  kosCreditorLoading ||
                  (!hasSignedAgreement && !skipSignature) ||
                  !isFormValid
                }
              >
                <OnboardingButtonOutlined
                  onClick={handleSave}
                  disabled={
                    kosCreditorLoading ||
                    (!hasSignedAgreement && !skipSignature) ||
                    !isFormValid
                  }
                >
                  <ButtonContent>
                    {kosCreditorLoading && <Spinner />}
                    <LabelMedium>
                      {kosCreditorLoading ? "Lagrer..." : "Lagre"}
                    </LabelMedium>
                  </ButtonContent>
                </OnboardingButtonOutlined>
              </DisabledButtonState>
            </FlexRow>

            {!hasSignedAgreement && !skipSignature && (
              <FlexRow justifyContent="flex-end">
                <LabelMedium
                  style={{
                    fontSize: THEME.fontSize.sm,
                    color: THEME.colors.textMuted,
                    fontStyle: "italic",
                  }}
                >
                  Du må signere avtalen før du kan lagre bedriftsinformasjon
                </LabelMedium>
              </FlexRow>
            )}
          </FlexColumn>
        </FlexColumn>
      )}
    </OnboardingCard>
  );
};

export default OnboardingSetupForm;
